import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Loader2, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { migrationV1V2Api } from '../api/migration-v1-v2';
import type {
  ReferentialOverrideRow,
  ReferentialOverridesResponse,
} from '../api/migration-v1-v2';

/**
 * Liste des rôles tenant V2 (synchronisée avec V2 — cf. Migration/README).
 */
const V2_TENANT_ROLES: { code: string; label: string }[] = [
  { code: 'legal_guardian', label: 'Responsable légal' },
  { code: 'student', label: 'Élève' },
  { code: 'teacher', label: 'Enseignant' },
  { code: 'administrator', label: 'Administrateur' },
  { code: 'director', label: 'Directeur' },
  { code: 'network_director', label: 'Directeur de réseau' },
  { code: 'academic_director', label: 'Directeur pédagogique' },
  { code: 'executive_assistant', label: 'Assistant de direction' },
  { code: 'treasurer', label: 'Trésorier' },
  { code: 'accountant', label: 'Comptable' },
  { code: 'librarian', label: 'Bibliothécaire' },
  { code: 'association_president', label: "Président d'association" },
  { code: 'supervisor', label: 'Surveillant' },
  { code: 'gardener', label: 'Jardinier' },
  { code: 'technical_agent', label: 'Agent technique' },
  { code: 'communication_manager', label: 'Responsable communication' },
  { code: 'employer', label: 'Employeur' },
];

const NONE_VALUE = '__none__';

interface Props {
  schoolId: number;
}

interface EditState {
  targetCode: string | null;
  dirty: boolean;
  saving: boolean;
}

export function RoleMappingsTab({ schoolId }: Props) {
  const [data, setData] = useState<ReferentialOverridesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, EditState>>({});

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await migrationV1V2Api.getReferentialOverrides(schoolId, 'role', true);
      setData(resp);
      setEdits({});
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  const rows = useMemo(() => data?.mappings ?? [], [data]);

  const editFor = (row: ReferentialOverrideRow): EditState => {
    return (
      edits[row.sourceId] ?? {
        targetCode: row.targetCode,
        dirty: false,
        saving: false,
      }
    );
  };

  const setEdit = (sourceId: string, patch: Partial<EditState>) => {
    setEdits((prev) => ({
      ...prev,
      [sourceId]: {
        ...(prev[sourceId] ?? { targetCode: null, dirty: false, saving: false }),
        ...patch,
        dirty: true,
      },
    }));
  };

  const save = async (row: ReferentialOverrideRow) => {
    const e = editFor(row);
    setEdits((prev) => ({ ...prev, [row.sourceId]: { ...e, saving: true } }));
    try {
      const targetCodeForServer =
        e.targetCode && e.targetCode !== row.globalTargetCode ? e.targetCode : null;

      if (targetCodeForServer === null) {
        await migrationV1V2Api.deleteReferentialOverride(schoolId, 'role', row.sourceId);
      } else {
        await migrationV1V2Api.upsertReferentialOverride(schoolId, {
          type: 'role',
          sourceId: row.sourceId,
          targetCode: targetCodeForServer,
        });
      }
      await load();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setEdits((prev) => ({ ...prev, [row.sourceId]: { ...e, saving: false } }));
    }
  };

  const resetToGlobal = async (row: ReferentialOverrideRow) => {
    if (!row.hasOverride) return;
    if (!confirm(`Supprimer l'override pour "${row.sourceLabel}" et revenir au mapping global ?`)) {
      return;
    }
    try {
      await migrationV1V2Api.deleteReferentialOverride(schoolId, 'role', row.sourceId);
      await load();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500" style={{ textTransform: 'none' }}>
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Chargement des mappings…
      </div>
    );
  }

  return (
    <div className="space-y-4" style={{ textTransform: 'none' }}>
      {/* Bandeau d'info — texte contrasté lisible */}
      <div
        className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
        style={{ color: '#78350f' }}
      >
        <div className="font-semibold mb-1 flex items-center gap-2 text-sm" style={{ color: '#78350f' }}>
          <AlertTriangle className="h-4 w-4" />
          Override des mappings pour cette école
        </div>
        <p className="text-sm leading-relaxed" style={{ color: '#92400e', textTransform: 'none', fontSize: '0.8125rem', fontWeight: 400, letterSpacing: 'normal', display: 'block' }}>
          Le mapping global reste appliqué en fallback pour les rôles non-overridés.
          Choisir un rôle V2 différent du global = override stocké pour cette école.
          Sélectionner « (retour au global) » = suppression de l'override.
          {data && !data.v1Live.available && (
            <span className="block mt-1 text-amber-700">
              ⚠️ Lecture live de la table V1 <code>usertype</code> indisponible
              {data.v1Live.error ? ` (${data.v1Live.error})` : ''}.
            </span>
          )}
        </p>
      </div>

      {error && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm"
          style={{ color: '#991b1b' }}
        >
          {error}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '48px' }} />
            <col style={{ width: '30%' }} />
            <col style={{ width: '32%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '20%' }} />
          </colgroup>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th
                className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200"
                style={{ textTransform: 'none', fontSize: '0.75rem', letterSpacing: 'normal' }}
              >
                ID
              </th>
              <th
                className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200"
                style={{ textTransform: 'none', fontSize: '0.75rem', letterSpacing: 'normal' }}
              >
                Libellé V1
              </th>
              <th
                className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200"
                style={{ textTransform: 'none', fontSize: '0.75rem', letterSpacing: 'normal' }}
              >
                Rôle V2 (override)
              </th>
              <th
                className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200"
                style={{ textTransform: 'none', fontSize: '0.75rem', letterSpacing: 'normal' }}
              >
                Statut
              </th>
              <th
                className="px-3 py-2.5 text-right font-medium text-gray-700 border-b border-gray-200"
                style={{ textTransform: 'none', fontSize: '0.75rem', letterSpacing: 'normal' }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const e = editFor(row);
              const effectiveCode = e.targetCode ?? row.globalTargetCode ?? null;
              const isOverridden = row.hasOverride;
              const differsFromGlobal =
                e.targetCode !== null && e.targetCode !== row.globalTargetCode;
              const v1Label =
                row.v1LiveLabel ?? row.sourceLabel ?? row.globalSourceLabel ?? '—';
              const v2RoleLabel = effectiveCode
                ? V2_TENANT_ROLES.find((r) => r.code === effectiveCode)?.label
                : null;

              return (
                <tr
                  key={row.sourceId}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  <td
                    className="px-3 py-2 font-mono text-xs text-gray-500 align-middle"
                    style={{ textTransform: 'none' }}
                  >
                    {row.sourceId}
                  </td>
                  <td className="px-3 py-2 align-middle">
                    <div className="text-gray-900 font-medium" style={{ textTransform: 'none' }}>
                      {v1Label}
                    </div>
                    {row.v1LiveLabel &&
                      row.globalSourceLabel &&
                      row.v1LiveLabel !== row.globalSourceLabel && (
                        <div className="text-xs text-gray-500 mt-0.5" style={{ textTransform: 'none' }}>
                          Global : {row.globalSourceLabel}
                        </div>
                      )}
                  </td>
                  <td className="px-3 py-2 align-middle">
                    <Select
                      value={e.targetCode ?? NONE_VALUE}
                      onValueChange={(v) =>
                        setEdit(row.sourceId, { targetCode: v === NONE_VALUE ? null : v })
                      }
                    >
                      <SelectTrigger className="h-9 text-sm" style={{ textTransform: 'none' }}>
                        <SelectValue>
                          {effectiveCode ? (
                            <span style={{ textTransform: 'none' }}>
                              {v2RoleLabel ?? effectiveCode}
                            </span>
                          ) : (
                            <span className="text-gray-400">(non mappé)</span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_VALUE}>
                          <span style={{ textTransform: 'none' }}>
                            (retour au global
                            {row.globalTargetCode ? ` — ${row.globalTargetCode}` : ''})
                          </span>
                        </SelectItem>
                        {V2_TENANT_ROLES.map((r) => (
                          <SelectItem key={r.code} value={r.code}>
                            <span style={{ textTransform: 'none' }}>
                              {r.label}{' '}
                              <span className="text-gray-400 text-xs font-mono">
                                {r.code}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 align-middle">
                    {isOverridden ? (
                      <Badge
                        className="bg-amber-100 border-amber-200"
                        style={{ color: '#92400e', textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        Override
                      </Badge>
                    ) : effectiveCode ? (
                      <Badge variant="outline" style={{ color: '#6b7280', textTransform: 'none', fontSize: '0.75rem' }}>
                        Global
                      </Badge>
                    ) : (
                      <Badge
                        className="bg-red-100 border-red-200"
                        style={{ color: '#991b1b', textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        Non mappé
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right align-middle">
                    <div className="inline-flex items-center gap-1">
                      {e.dirty && differsFromGlobal && (
                        <Button size="sm" variant="default" onClick={() => save(row)} disabled={e.saving}>
                          {e.saving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Save className="h-3.5 w-3.5" />
                          )}
                          Enregistrer
                        </Button>
                      )}
                      {e.dirty && !differsFromGlobal && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => save(row)}
                          disabled={e.saving}
                          title="Retour au global (supprime l'override)"
                        >
                          {e.saving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Check className="h-3.5 w-3.5" />
                          )}
                          Valider
                        </Button>
                      )}
                      {isOverridden && !e.dirty && (
                        <Button size="sm" variant="ghost" onClick={() => resetToGlobal(row)}>
                          <RotateCcw className="h-3.5 w-3.5" />
                          Reset
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-gray-500" style={{ textTransform: 'none' }}>
                  Aucun mapping disponible. Initialisez d'abord les rôles globaux via{' '}
                  <code>/migration-v1-v2/config</code>.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
